import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useAuth } from '../../contexts/AuthContext';
import { sitesApi } from '../../components/sites/sitesApi';

const card = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: '12px',
  padding: '2rem',
  maxWidth: 640,
  margin: '0 auto',
};

const primaryBtn = (disabled) => ({
  padding: '0.75rem 2rem',
  background: disabled ? '#9ca3af' : 'var(--ink, #171A0D)',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  fontSize: '1rem',
  fontWeight: 600,
  cursor: disabled ? 'not-allowed' : 'pointer',
  width: '100%',
});

const secondaryBtn = {
  padding: '0.75rem 2rem',
  background: 'transparent',
  color: '#6b7280',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  fontSize: '0.95rem',
  cursor: 'pointer',
  width: '100%',
  marginTop: '0.75rem',
};

const ALL_LOOKS = ['bold-block', 'editorial', 'bento', 'soft-modern', 'swiss-minimal'];

const BUILD_LOG_PACE_MS = 400;

const FAKE_WRITER_MSG = 'The AI writer is not enabled in this environment.';

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** S11d.1: wizard "other" uses brief category `other` → general pack at generate time. */
function categoryForGenerate(pick) {
  if (pick === 'dental') return 'dental';
  if (pick === 'roofing') return 'roofing';
  return 'other';
}

function ownerFacingSitesError(message, kind = 'build') {
  const raw = message || (kind === 'import' ? 'Import failed' : 'Build failed');
  if (/not enabled in this environment/i.test(raw)) return FAKE_WRITER_MSG;
  if (/ENOENT|no such file|\.json|\/Users\/|node_modules|Unknown brief category pack/i.test(raw)) {
    return kind === 'import'
      ? 'We could not read that site. Check the URL or answer a few questions instead.'
      : FAKE_WRITER_MSG;
  }
  if (/\/Users\/|at .*\.(?:js|ts):\d+/i.test(raw)) {
    return kind === 'import'
      ? 'We could not read that site. Check the URL or answer a few questions instead.'
      : 'We could not finish building your site. Try again or answer a few questions instead.';
  }
  if (/^[A-Za-z]+Error:|ECONNREF|ETIMEDOUT|fetch failed/i.test(raw)) {
    return kind === 'import'
      ? 'We could not read that site. Check the URL or answer a few questions instead.'
      : 'We could not finish building your site. Try again or answer a few questions instead.';
  }
  if (raw.length > 180) {
    return kind === 'import'
      ? 'We could not read that site. Check the URL or answer a few questions instead.'
      : 'We could not finish building your site. Try again or answer a few questions instead.';
  }
  return raw;
}

function pickGalleryLooks(ranking, seed) {
  const out = [];
  for (const id of [...(ranking || []), ...ALL_LOOKS]) {
    if (!out.includes(id) && out.length < 6) out.push(id);
  }
  while (out.length < 6) {
    out.push(ALL_LOOKS[out.length % ALL_LOOKS.length]);
  }
  return out.slice(0, 6).map((look, i) => ({ look, variant: `${seed || 'gallery'}-${i}-${look}` }));
}

export default function NewSiteWizard() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { selectedWorkspaceId, loading: workspaceLoading } = useWorkspace();
  const workspaceId = currentUser?.uid || selectedWorkspaceId;

  const [step, setStep] = useState(1);
  const [hasSite, setHasSite] = useState(null);
  const [url, setUrl] = useState('');
  const [logLines, setLogLines] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const [businessName, setBusinessName] = useState('');
  const [category, setCategory] = useState('roofing');
  const [cityState, setCityState] = useState('');
  const [phone, setPhone] = useState('');
  const [serviceInput, setServiceInput] = useState('');
  const [serviceChips, setServiceChips] = useState([]);
  const [people, setPeople] = useState([{ name: '', role: '' }]);
  const [pendingImport, setPendingImport] = useState(null);
  const [importCategory, setImportCategory] = useState('other');
  const [showQuestionsFallback, setShowQuestionsFallback] = useState(false);
  const [lastImportForFallback, setLastImportForFallback] = useState(null);

  const pushLog = (line) => setLogLines((prev) => [...prev, line]);

  async function runBuild(importResult, briefOverride) {
    if (!workspaceId) {
      setError('No workspace selected');
      return;
    }
    setStep(3);
    setBusy(true);
    setError(null);
    setLogLines([]);
    try {
      let brief = briefOverride;
      let facts = [];
      let photos = [];
      if (importResult) {
        setLastImportForFallback(importResult);
        const pageCount = importResult.pages?.length || 0;
        pushLog(`Reading ${pageCount} pages…`);
        await sleep(BUILD_LOG_PACE_MS);
        const peopleN = importResult.brief?.people?.length || 0;
        const svcN = importResult.brief?.services?.length || 0;
        const ansN = importResult.brief?.faqs?.length || 0;
        pushLog(`Found ${peopleN} people, ${svcN} services, ${ansN} answers`);
        await sleep(BUILD_LOG_PACE_MS);
        brief = importResult.brief;
        facts = importResult.facts || [];
        photos = importResult.photos || [];
      } else {
        pushLog('Using your answers…');
        await sleep(BUILD_LOG_PACE_MS);
      }
      pushLog('Writing your copy…');
      await sleep(BUILD_LOG_PACE_MS);
      const seed = 'wizard-ui';
      const gen = await sitesApi.generateSite(workspaceId, brief, seed);
      pushLog('Checking every fact…');
      await sleep(BUILD_LOG_PACE_MS);
      pushLog('Rendering…');
      await sleep(BUILD_LOG_PACE_MS);
      const mainRender = await sitesApi.renderSite(workspaceId, gen.siteId, { look: gen.report?.look });
      const gallery = pickGalleryLooks(gen.report?.ranking, seed);
      const handoff = {
        previewUrl: mainRender.previewUrl,
        report: gen.report,
        facts: facts.length ? facts : gen.facts || [],
        photos: photos.length ? photos : importResult?.photos || [],
        brief,
        gallery,
        workspaceId,
      };
      try {
        sessionStorage.setItem(`sites-handoff-${gen.siteId}`, JSON.stringify(handoff));
      } catch {
        /* ignore quota */
      }
      navigate(`/sites/${gen.siteId}`, {
        replace: true,
        state: handoff,
      });
    } catch (e) {
      const friendly = ownerFacingSitesError(e.message, 'build');
      setError(friendly);
      setBusy(false);
      if (friendly === FAKE_WRITER_MSG) {
        setShowQuestionsFallback(true);
      }
    }
  }

  async function startImport() {
    if (!url.trim()) return;
    setBusy(true);
    setError(null);
    setLogLines(['Reading pages…']);
    try {
      const imp = await sitesApi.importSite(workspaceId, url.trim());
      setLogLines([`Found ${imp.pages?.length || 0} pages`, 'Reading pages…']);
      setBusy(false);
      const cat = imp.brief?.business?.category;
      if (cat === 'unknown' || !cat) {
        setPendingImport(imp);
        setImportCategory('other');
        setStep('category');
        return;
      }
      await runBuild(imp);
    } catch (e) {
      setError(ownerFacingSitesError(e.message, 'import'));
      setBusy(false);
    }
  }

  function applyImportCategoryAndBuild() {
    if (!pendingImport) return;
    const pick = importCategory;
    const brief = {
      ...pendingImport.brief,
      business: {
        ...pendingImport.brief.business,
        category: categoryForGenerate(pick),
      },
    };
    const imp = { ...pendingImport, brief };
    setPendingImport(null);
    setStep(3);
    runBuild(imp);
  }

  function startQuestionsFromImportFallback() {
    const imp = lastImportForFallback || pendingImport;
    setShowQuestionsFallback(false);
    setError(null);
    setPendingImport(null);
    if (imp?.brief?.business?.name) {
      setBusinessName(imp.brief.business.name);
    }
    if (imp?.brief?.contact?.city || imp?.brief?.contact?.region) {
      const c = imp.brief.contact;
      setCityState([c.city, c.region].filter(Boolean).join(', '));
    }
    if (imp?.brief?.contact?.phones?.[0]) {
      setPhone(String(imp.brief.contact.phones[0]));
    }
    if (imp?.brief?.services?.length) {
      setServiceChips(imp.brief.services.slice(0, 5).map((s) => s.name).filter(Boolean));
    }
    if (imp?.brief?.people?.length) {
      setPeople(
        imp.brief.people.slice(0, 5).map((p) => ({
          name: p.name || '',
          role: p.role || '',
        }))
      );
    }
    setCategory(importCategory || 'other');
    setHasSite(false);
    setStep(2);
  }

  function buildBriefFromQuestions() {
    const [cityPart, regionPart] = cityState.split(',').map((s) => s.trim());
    const services = [...serviceChips];
    const trimmed = serviceInput.trim();
    if (trimmed && !services.includes(trimmed) && services.length < 5) services.push(trimmed);
    return {
      business: { name: businessName.trim(), category: categoryForGenerate(category) },
      contact: {
        phones: phone.trim() ? [phone.trim()] : [],
        city: cityPart || cityState.trim(),
        region: regionPart || '',
        serviceArea: cityState.trim(),
      },
      services: services.slice(0, 5).map((name) => ({ name })),
      people: people
        .filter((p) => p.name.trim())
        .map((p) => ({ name: p.name.trim(), role: p.role.trim() || 'Owner' })),
    };
  }

  function addPersonRow() {
    setPeople((prev) => [...prev, { name: '', role: '' }]);
  }

  function updatePerson(index, field, value) {
    setPeople((prev) => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
  }

  function addServiceChip() {
    const name = serviceInput.trim();
    if (!name || serviceChips.length >= 5) return;
    if (serviceChips.includes(name)) return;
    setServiceChips((prev) => [...prev, name]);
    setServiceInput('');
  }

  function handleQuestionsContinue() {
    runBuild(null, buildBriefFromQuestions());
  }

  if (workspaceLoading || !workspaceId) {
    return (
      <div style={{ padding: '2rem 1.5rem' }}>
        <div style={card}>
          <p>Loading workspace…</p>
        </div>
      </div>
    );
  }

  if (step === 3) {
    return (
      <div style={{ padding: '2rem 1.5rem' }}>
        <div style={card}>
          <h2 style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>Building your site</h2>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, color: '#374151', lineHeight: 1.8 }}>
            {logLines.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
            {busy && !logLines.length && <li>Starting…</li>}
          </ul>
          {error && <p style={{ color: 'var(--danger, #B4472F)', marginTop: '1rem' }}>{error}</p>}
          {showQuestionsFallback && (
            <button type="button" style={{ ...secondaryBtn, marginTop: '1rem' }} onClick={startQuestionsFromImportFallback}>
              Answer a few questions instead
            </button>
          )}
        </div>
      </div>
    );
  }

  if (step === 'category' && pendingImport) {
    return (
      <div style={{ padding: '2rem 1.5rem' }}>
        <div style={card}>
          <h2 style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>What kind of business is this?</h2>
          <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
            Pick a category so we can recommend a design (roofing, dental, or other).
          </p>
          <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
            <legend style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden' }}>Category</legend>
            {[
              { value: 'roofing', label: 'Roofing' },
              { value: 'dental', label: 'Dental' },
              { value: 'other', label: 'Other' },
            ].map(({ value, label }) => (
              <label
                key={value}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginBottom: '0.65rem',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                <input
                  type="radio"
                  name="import-category"
                  value={value}
                  checked={importCategory === value}
                  onChange={() => setImportCategory(value)}
                />
                {label}
              </label>
            ))}
          </fieldset>
          <button
            type="button"
            style={primaryBtn(busy)}
            disabled={busy}
            onClick={applyImportCategoryAndBuild}
          >
            Build my site
          </button>
          {error && <p style={{ color: 'var(--danger, #B4472F)', marginTop: '1rem' }}>{error}</p>}
        </div>
      </div>
    );
  }

  if (step === 2 && hasSite === true) {
    return (
      <div style={{ padding: '2rem 1.5rem' }}>
        <div style={card}>
          <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>Website URL</h2>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.75rem' }}>
            Website URL
            <input
              type="url"
              name="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && url.trim() && !busy) {
                  e.preventDefault();
                  startImport();
                }
              }}
              placeholder="https://example.com"
              style={{ display: 'block', width: '100%', marginTop: 4, padding: '0.6rem', borderRadius: 8, border: '1px solid #e5e7eb' }}
            />
          </label>
          <button type="button" style={primaryBtn(busy || !url.trim())} disabled={busy || !url.trim()} onClick={startImport}>
            Read my site
          </button>
          {(busy || logLines.length > 0) && (
            <p style={{ marginTop: '1rem', color: '#374151' }}>
              {logLines.join(' · ') || 'Reading pages…'}
            </p>
          )}
          {error && <p style={{ color: 'var(--danger, #B4472F)', marginTop: '1rem' }}>{error}</p>}
        </div>
      </div>
    );
  }

  if (step === 2 && hasSite === false) {
    return (
      <div style={{ padding: '2rem 1.5rem' }}>
        <div style={card}>
          <h2 style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>Tell us about your business</h2>
          <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: 600 }}>
            Business name
            <input
              type="text"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              style={{ display: 'block', width: '100%', marginTop: 4, padding: '0.6rem', borderRadius: 8, border: '1px solid #e5e7eb' }}
            />
          </label>
          <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: 600 }}>
            Category
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              style={{ display: 'block', width: '100%', marginTop: 4, padding: '0.6rem', borderRadius: 8, border: '1px solid #e5e7eb' }}
            >
              <option value="roofing">Roofing</option>
              <option value="dental">Dental</option>
              <option value="other">Other</option>
            </select>
          </label>
          <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: 600 }}>
            City and state
            <input
              type="text"
              value={cityState}
              onChange={(e) => setCityState(e.target.value)}
              placeholder="Riverton, CO"
              style={{ display: 'block', width: '100%', marginTop: 4, padding: '0.6rem', borderRadius: 8, border: '1px solid #e5e7eb' }}
            />
          </label>
          <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: 600 }}>
            Phone
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              style={{ display: 'block', width: '100%', marginTop: 4, padding: '0.6rem', borderRadius: 8, border: '1px solid #e5e7eb' }}
            />
          </label>
          <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: 600 }}>
            Services (add up to 5)
            <input
              type="text"
              value={serviceInput}
              onChange={(e) => setServiceInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addServiceChip();
                }
              }}
              placeholder="Roof repair"
              style={{ display: 'block', width: '100%', marginTop: 4, padding: '0.6rem', borderRadius: 8, border: '1px solid #e5e7eb' }}
            />
          </label>
          {serviceChips.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
              {serviceChips.map((s) => (
                <span
                  key={s}
                  className="service-chip"
                  data-chip
                  style={{
                    padding: '0.35rem 0.65rem',
                    borderRadius: 999,
                    background: '#f3f4f6',
                    fontSize: '0.85rem',
                    color: '#374151',
                  }}
                >
                  {s}
                </span>
              ))}
            </div>
          )}
          <p style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Who should visitors meet?</p>
          {people.map((person, index) => (
            <div key={index} style={{ marginBottom: '0.75rem', paddingBottom: '0.75rem', borderBottom: index < people.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                Name
                <input
                  type="text"
                  value={person.name}
                  onChange={(e) => updatePerson(index, 'name', e.target.value)}
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: '0.6rem', borderRadius: 8, border: '1px solid #e5e7eb' }}
                />
              </label>
              <label style={{ display: 'block', marginBottom: 0, fontWeight: 600 }}>
                Role
                <input
                  type="text"
                  value={person.role}
                  onChange={(e) => updatePerson(index, 'role', e.target.value)}
                  placeholder="Owner"
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: '0.6rem', borderRadius: 8, border: '1px solid #e5e7eb' }}
                />
              </label>
            </div>
          ))}
          <button
            type="button"
            onClick={addPersonRow}
            style={{
              marginBottom: '1rem',
              padding: '0.5rem 0.85rem',
              borderRadius: 8,
              border: '1px solid #e5e7eb',
              background: '#fff',
              cursor: 'pointer',
              fontWeight: 600,
              color: '#374151',
            }}
          >
            Add person
          </button>
          <button type="button" style={primaryBtn(busy || !businessName.trim())} disabled={busy || !businessName.trim()} onClick={handleQuestionsContinue}>
            Build my site
          </button>
          {error && <p style={{ color: 'var(--danger, #B4472F)', marginTop: '1rem' }}>{error}</p>}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem 1.5rem' }}>
      <div style={card}>
        <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>Do you have a website?</h2>
        {hasSite === null && (
          <>
            <button type="button" style={primaryBtn(false)} onClick={() => { setHasSite(true); setStep(2); }}>
              Yes
            </button>
            <button type="button" style={secondaryBtn} onClick={() => { setHasSite(false); setStep(2); }}>
              No
            </button>
          </>
        )}
        {error && <p style={{ color: 'var(--danger, #B4472F)', marginTop: '1rem' }}>{error}</p>}
      </div>
    </div>
  );
}
